<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useSessionStore } from '../../stores/session-store';

const emit = defineEmits<{
  (e: 'resolved'): void;
}>();

const sessionStore = useSessionStore();
const isResolving = ref(false);
const error = ref<string | null>(null);

const hasQrToken = computed(() => {
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get('qr_token')) || Boolean(window.document.cookie.match(/(?:(?:^|.*;\s*)pending_qr_token\s*=\s*([^;]*).*$)|^.*$/)?.[1]);
});

onMounted(async () => {
  if (hasQrToken.value) {
    isResolving.value = true;
    try {
      // sessionStore.bootstrapSessionContext already handles qr_token internally, 
      // but if we abstract it here, we might just wait for it if it's already doing it,
      // or we do it explicitly. Since App.vue calls bootstrapSessionContext() anyway,
      // we can just watch sessionStore.sessionReady or bootstrapCompleted.
      
      // Wait, we can actually trigger the specific logic here.
      // But App.vue already triggered bootstrapSessionContext().
      // For now, this component just acts as a UI overlay while sessionStore is loading
      // specifically when a QR token is present.
    } catch (err: any) {
      error.value = err.message || 'Error al procesar el código QR';
    } finally {
      isResolving.value = false;
    }
  }
});

const retry = () => {
  window.location.reload();
};
</script>

<template>
  <div v-if="hasQrToken && (sessionStore.loading || sessionStore.error)" class="qr-resolver-overlay">
    <div class="qr-card">
      <div v-if="sessionStore.error" class="qr-error" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="icon" aria-hidden="true">❌</div>
        <h2>Código Inválido</h2>
        <p>{{ sessionStore.error }}</p>
        <button class="btn btn-primary" @click="retry">Reintentar</button>
      </div>
      <div v-else class="qr-resolving" role="status" aria-live="polite" aria-atomic="true">
        <div class="spinner" aria-hidden="true"></div>
        <h2>Preparando tu mesa...</h2>
        <p>Estamos validando tu sesión, un momento por favor.</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qr-resolver-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.qr-card {
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  width: 90%;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.qr-resolving .spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.qr-resolving h2, .qr-error h2 {
  margin: 0 0 0.5rem 0;
  color: #0f172a;
  font-size: 1.5rem;
}

.qr-resolving p, .qr-error p {
  margin: 0;
  color: #64748b;
  font-size: 1rem;
}

.qr-error .icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.qr-error .btn {
  margin-top: 1.5rem;
  width: 100%;
  padding: 0.75rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: bold;
  cursor: pointer;
}
</style>
